import { Container, Graphics } from 'pixi.js';
import { place, type RoomContext, type RoomDecorator } from './index';
import { registerScenery, synthCtx } from '../scenery';

// Register the CEO office's bespoke furniture as editable scenery kinds (passive metadata for the
// level editor — the live game still renders via the decorator below). Both accent lamps share one
// kind (identical build); the open laptop is its own kind. The leather blotter (`deskTop`) is added
// directly to the container, NOT via place(), so it is intentionally left unregistered.
registerScenery('accent-lamp', (art) => accentColumn(synthCtx(art)), 'Accent lamp', '🛋️');
registerScenery('desk-laptop', (art) => laptop(synthCtx(art)), 'Desk laptop', '💻');

/**
 * CEO OFFICE — an isolated (sealed-audio) executive corner office, dressed to feel IMPORTANT and a
 * little plush. Room rect (Walls + Rooms floorplan): x 1120→1560, y 280→580 (interior
 * 1120..1560 × 280..580, centre 1340,430).
 *
 * One door to respect:
 *  - W door (left wall, opening y 350→510): the lone entrance from the open-space hub. We keep that
 *    left-wall band wide open and leave a clean W→E approach lane so a duck walks straight in.
 *
 * DECLUTTER + SIT-EVERYWHERE pass (order + style):
 *  - ONE neat rug only — it grounds the GUEST LOUNGE group (the desk zone reads clean on bare floor,
 *    no second rug-blob). It's inset and aligned to the lounge seating.
 *  - TWO deliberate statement plants: a tall palm anchoring the NE prestige corner and one leafy plant
 *    softening the SE lounge corner. No scattered ferns/blobs anywhere else.
 *
 * Functional zoning of the 440×300 shell (no empty quadrants):
 *  - HERO (centre): the executive DESK with the high-back chair behind it. The exec CHAIR is SITTABLE,
 *    seat anchored at (1300,470) facing UP (−π/2), so the desk sits just above at ~(1300,430) and the
 *    duck "sits" into the desk — laptop + monitor + desk accessories dress the slab.
 *  - PRESTIGE WALL (north, behind the boss): a credenza centred behind the desk with framed art above,
 *    and an AWARDS CABINET + tall BOOKSHELF along the back/EAST corner for that trophy-wall status.
 *  - GUEST LOUNGE (east / front-right): a SITTABLE sofa + armchair facing off across a coffee table on
 *    the single rug — "where you'd be received" — tucked east of the desk, clear of the west door lane.
 *    Sofa seat anchor (1518,470) facing WEST (π); armchair seat anchor (1428,470) facing EAST (0).
 *  - A brass column lamp pools warm light by the desk and another over the guest lounge.
 *
 * SEAT ANCHORS (id, x, y, facing, kind) — every seat in the room is sittable:
 *   ceo-chair       1300, 470, −1.5708, chair     (faces UP into the executive desk)
 *   ceo-sofa        1518, 470,  3.1416, sofa       (faces WEST into the lounge)
 *   ceo-armchair    1428, 470,  0,      armchair   (faces EAST toward the sofa)
 *
 * Built ONCE on map build/re-skin (RoomDecorator contract) — static scene, no per-frame work.
 */

/** A slim brass-toned floor-standing accent lamp scaled for a corner (taller, narrower than f.lamp). */
function accentColumn(ctx: RoomContext): Container {
  const { art, mix, darken, lighten } = ctx;
  const c = new Container();
  const metal = mix(0x9a7a3a, art.accent, 0.3);
  // Warm pool of light on the floor (premium uplighter glow).
  c.addChild(new Graphics().ellipse(0, 16, 22, 9).fill({ color: art.glow, alpha: 0.1 * art.glowStrength + 0.05 }));
  c.addChild(
    new Graphics()
      .ellipse(0, 18, 7, 3)
      .fill(darken(metal, 0.3))
      .rect(-1.5, -30, 3, 48)
      .fill(metal)
      // small open shade glowing at the top
      .poly([-8, -30, 8, -30, 5, -42, -5, -42])
      .fill(mix(0xf6ecd0, art.accent, 0.12))
      .rect(-7, -31, 14, 2.5)
      .fill({ color: lighten(art.glow, 0.1), alpha: 0.9 }),
  );
  return c;
}

/** A leather desk blotter + closed laptop + a tidy stack of papers — the "executive at work" touch. */
function deskTop(ctx: RoomContext): Container {
  const { art, mix, darken, lighten } = ctx;
  const c = new Container();
  const leather = darken(mix(0x5a3a22, art.accent, 0.2), 0.05);
  c.addChild(
    new Graphics()
      // leather blotter
      .roundRect(-30, -7, 60, 18, 3)
      .fill(leather)
      .roundRect(-30, -7, 60, 4, 3)
      .fill({ color: 0xffffff, alpha: 0.08 })
      // a neat stack of papers, top-left of the blotter
      .roundRect(-26, -4, 16, 11, 1.5)
      .fill({ color: 0xf4f1e8, alpha: 0.95 })
      .roundRect(-26, -4, 16, 2, 1.5)
      .fill({ color: 0xffffff, alpha: 0.5 })
      // a small desk pen-pot / accent on the right
      .roundRect(20, -6, 7, 9, 2)
      .fill(mix(0x2a2f3a, art.accent, 0.15))
      .circle(23.5, -7, 1.6)
      .fill({ color: lighten(art.glow, 0.1), alpha: 0.9 }),
  );
  return c;
}

/**
 * A slim open LAPTOP sitting on the desk surface, screen glowing — the boss's "at work" centrepiece.
 * Origin at its base centre; the lid tilts back (up the screen) so it reads from the front.
 */
function laptop(ctx: RoomContext): Container {
  const { art, lighten } = ctx;
  const c = new Container();
  c.addChild(
    new Graphics()
      // base / keyboard deck
      .roundRect(-13, -2, 26, 5, 1.5)
      .fill(0x2a2f3a)
      .roundRect(-13, -2, 26, 1.5, 1.5)
      .fill({ color: 0xffffff, alpha: 0.12 })
      // tilted-back lid + lit screen
      .roundRect(-12, -18, 24, 16, 1.5)
      .fill(0x1b1f29)
      .roundRect(-10, -16, 20, 12, 1)
      .fill({ color: lighten(art.glow, 0.12), alpha: 0.95 })
      .rect(-8, -13, 10, 1.5)
      .fill({ color: 0xffffff, alpha: 0.35 })
      .rect(-8, -10, 13, 1.5)
      .fill({ color: 0xffffff, alpha: 0.22 }),
  );
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { container, art, f, lighten } = ctx;

  // Authoritative rect so every piece derives from geometry (rides any future move).
  const x0 = ctx.zone.x; // 1120
  const y0 = ctx.zone.y; // 280
  const right = x0 + ctx.zone.width; // 1560
  const bottom = y0 + ctx.zone.height; // 580

  // ── HERO: the executive desk + high-back chair. Anchored so "sit" lines up exactly. ────────────────
  // Chair seat at (1300,470) facing UP; desk slab just above at (1300,430); the duck sits into the desk.
  // The desk zone reads clean on bare floor — the room's single rug grounds the guest lounge instead.
  const deskX = 1300;
  const deskY = 430;
  const chairY = 470;

  // SITTABLE high-back exec chair at the seat anchor (1300,470) facing UP (−π/2) into the desk. It sorts
  // behind the slab (origin lower y than its tall back), so the duck reads as seated at the desk.
  place(ctx, f.officeChair(art), deskX, chairY);
  // The wide executive desk slab facing the room.
  place(ctx, f.longDesk(art, 132), deskX, deskY);
  // Dress the surface: leather blotter + papers + pen-pot, an open laptop, and a monitor angled out.
  const top = deskTop(ctx);
  top.x = deskX;
  top.y = deskY - 6;
  container.addChild(top);
  place(ctx, laptop(ctx), deskX - 2, deskY - 4, 'desk-laptop');
  place(ctx, f.monitor(art), deskX + 44, deskY - 6);
  // The boss's tiny "online" status glow on the desk — a premium detail.
  container.addChild(
    new Graphics()
      .circle(deskX - 52, deskY - 4, 4)
      .fill({ color: art.glow, alpha: 0.7 })
      .circle(deskX - 52, deskY - 4, 2)
      .fill({ color: lighten(art.glow, 0.25), alpha: 0.95 }),
  );

  // ── PRESTIGE WALL along the NORTH (behind the boss): credenza + framed art + a trophy run. ─────────
  // Sits high (low y) so the y-depth sort keeps it behind the desk + chair. Bodies stay ≥16px off walls.
  place(ctx, f.cabinet(art), deskX, y0 + 30); // credenza centred behind the desk (x 1266→1334)
  // Two framed works above the credenza, flanking centre on the back wall (hung high, body clear of it).
  place(ctx, f.wallArt(art, 'painting'), deskX - 56, y0 + 26);
  place(ctx, f.wallArt(art, 'abstract'), deskX + 56, y0 + 26);

  // Awards cabinet + tall bookshelf filling the NE corner (the trophy wall) — east of the desk zone.
  place(ctx, f.cabinet(art), right - 64, y0 + 30); // low awards credenza on the north wall (x 1462→1530)
  place(ctx, f.bookshelf(art), right - 30, y0 + 96); // tall shelves down the east wall, NE corner
  // DELIBERATE PLANT 1/2: a statement tall palm anchoring the deep NE prestige corner behind everything.
  place(ctx, f.plant(art, 'tall'), right - 26, y0 + 30);

  // ── GUEST LOUNGE in the EAST / front-right — "where you'd be received", clear of the west door. ────
  // The room's ONE rug grounds a sofa + armchair facing off across a coffee table. Hugs high x so it
  // never crowds the west entrance lane (door y350..510 on the LEFT wall, far across the room).
  const loungeCx = 1466; // lounge centre x (sofa↔armchair midline near here)
  const loungeCy = 470; // shares the seat-row y with the exec chair — a calm, level reading
  // The SINGLE neat rug, inset + aligned, sized to sit under both seats and the table (stays in rect).
  place(ctx, f.rug(art, 180, 140), loungeCx, loungeCy);

  // SITTABLE SOFA at the seat anchor (1518,470) along the EAST wall, FRONT facing WEST (π) into the room.
  // Built facing 'down' (front = +y local); rotation +π/2 maps local +y → world −x, matching facing = π.
  const sofa = place(ctx, f.sofa(art), 1518, loungeCy);
  sofa.rotation = Math.PI / 2;
  // SITTABLE ARMCHAIR at the seat anchor (1428,470), FRONT facing EAST (0) toward the sofa across the
  // table. Rotation −π/2 maps local +y → world +x, matching facing = 0.
  const armchair = place(ctx, f.armchair(art), 1428, loungeCy);
  armchair.rotation = -Math.PI / 2;
  // A low coffee table dead between the two seats — the heart of the receiving group, on the rug centre.
  place(ctx, f.coffeeTable(art), loungeCx, loungeCy);
  // DELIBERATE PLANT 2/2: one leafy plant softening the SE lounge corner (the only other greenery).
  place(ctx, f.plant(art, 'leafy'), right - 28, bottom - 34);

  // ── Warm accent lighting: a brass column lamp by the desk + one by the lounge for a plush, lit feel. ─
  place(ctx, accentColumn(ctx), deskX - 86, deskY - 4, 'accent-lamp'); // beside the desk, toward the entrance side
  place(ctx, accentColumn(ctx), loungeCx, bottom - 40, 'accent-lamp'); // pooling light along the south edge of the lounge
};

export default decorate;
